import { api, handleApiError } from './api';

export interface ClientData {
  id: string;
  name: string;
  status: string;
  email: string;
  phone: string;
  type: string;
  projectsShown: string;
  projectsList: string;
  pendingAction: string;
  lastMeeting: string;
  totalMeetings: string;
  isPendingActionHighlighted: boolean;
  // Additional fields from schema
  salesPerson?: string;
  channelPartner?: string;
  project_id?: string;
  budget?: number;
  nextMeetingDate?: string;
  rating?: number;
  is_active?: boolean;
  conversation_id?: string;
  request_id?: string;
  lastMeetingDate?: any;
  meetingCount?: number;
  address?: string;
}

export interface ClientListResponse {
  data: ClientData[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    limit: number;
  };
}

// Meeting-derived aggregation, loaded lazily per client so it never blocks the list.
export interface ClientMeetingSummary {
  totalMeetings: string;
  lastMeeting: string;
  projectsShown: string;
  projectsList: string;
  pendingAction: string;
  isPendingActionHighlighted: boolean;
  salesPerson?: string | null;
}

export interface MeetingData {
  id: string;
  meeting_id: string;
  conversation_id: string | null;
  request_id: string | null;
  name: string;
  client: string;
  clientId: string;
  date: string;
  project: string;
  salesExecutive: string;
  salesExecutiveId: string;
  duration: string;
  note: string;
  scorePercentage: number;
  pendingActions: Array<{
    id: string;
    text: string;
    dueDate?: string;
    assignedTo?: string;
  }>;
}

class ClientService {
  private clientIdCounter = 1000; // Start counter for fallback IDs

  // The profile page derives its data from a client's meetings several times in
  // quick succession (header aggregation in getClientById, the progressive
  // apiMeetings fetch, project derivation). Each was a separate /api/meetings
  // request (limit 500). This short-lived cache shares the in-flight/most-recent
  // promise so those identical calls collapse into ONE network round-trip.
  private meetingsCache = new Map<string, { promise: Promise<MeetingData[]>; timestamp: number }>();
  private static readonly MEETINGS_CACHE_TTL_MS = 30_000;

  private generateValidClientId(customer: any): string {
    // If customer.id exists and is a non-empty string, use it directly
    if (customer.id && typeof customer.id === 'string' && customer.id.trim().length > 0) {
      return customer.id.trim();
    }
    
    // If customer.id is a number, convert it to string
    if (customer.id && typeof customer.id === 'number') {
      return customer.id.toString();
    }
    
    // Try to parse as number for legacy numeric IDs
    const parsedId = parseInt(customer.id);
    if (!isNaN(parsedId) && parsedId > 0) {
      return parsedId.toString();
    }

    // Log the problematic customer data for debugging (this should rarely happen now)
    console.warn('Invalid or missing customer ID found:', customer.id, 'for customer:', customer.name || customer.email);
    
    // Use fallback counter for customers without valid IDs
    return `client_${this.clientIdCounter++}`;
  }

  private async apiCall<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    try {
      const response = await api.get(endpoint, { params });
      return response.data;
    } catch (error: any) {
      console.error(`API call failed for ${endpoint}:`, error);
      // Humanize the error before throwing
      handleApiError(error);
      throw error; // This won't be reached, but TypeScript needs it
    }
  }

  async getClients(params?: {
    type?: string;
    project_id?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
  }): Promise<ClientListResponse> {
    const rawResponse = await this.apiCall<any>('/api/clients', params);

    // Map raw customers directly to ClientData. Meeting-derived fields
    // (projects / pending actions) are loaded lazily per card so a single
    // list page costs ONE request instead of 1 + N meeting requests.
    const transformedData = rawResponse.data.map((customer: any) =>
      this.mapCustomerToClient(customer)
    );

    return {
      data: transformedData,
      pagination: rawResponse.pagination
    };
  }

  async getClientById(clientId: string | number): Promise<ClientData> {
    const rawClient = await this.apiCall<any>(`/api/clients/${clientId}`);
    return this.transformCustomerToClient(rawClient);
  }

  // Single-client path (profile pages) — keeps the full meeting-derived
  // aggregation it always had.
  private async transformCustomerToClient(customer: any, sessionType?: string): Promise<ClientData> {
    try {
      const meetings = await this.getClientMeetings(customer.id, sessionType);
      return this.mapCustomerToClient(customer, this.aggregateMeetings(meetings));
    } catch (error) {
      console.error('Error transforming customer data:', error);
      // Return basic data if transformation fails
      return this.mapCustomerToClient(customer);
    }
  }

  // Lazily fetched by each ClientCard so the grid renders immediately.
  async getClientMeetingSummary(clientId: string, type?: string): Promise<ClientMeetingSummary> {
    const meetings = await this.getClientMeetings(clientId, type);
    return this.aggregateMeetings(meetings);
  }

  // Build the meeting-derived aggregation from a client's meetings.
  private aggregateMeetings(meetings: MeetingData[]): ClientMeetingSummary {
    const totalMeetings = meetings.length;
    const lastMeeting = meetings.length > 0
      ? new Date(meetings[0].date).toLocaleDateString()
      : 'No meetings';

    // Get unique projects from meetings
    const projectsSet = new Set(meetings.map(m => m.project));
    const projects = Array.from(projectsSet).filter(Boolean);
    const projectsShown = projects.length.toString();
    const projectsList = projects.join(', ') || 'No projects';

    // Aggregate pending actions across meetings
    const pendingActions = meetings.reduce((acc, meeting) => {
      return acc.concat(meeting.pendingActions || []);
    }, [] as any[]);
    const pendingAction = pendingActions.length > 0
      ? `${pendingActions.length} pending`
      : 'No pending actions';
    const isPendingActionHighlighted = pendingActions.length > 0;

    const salesPerson = meetings.length > 0 ? meetings[0].salesExecutive : null;

    return {
      totalMeetings: totalMeetings.toString(),
      lastMeeting,
      projectsShown,
      projectsList,
      pendingAction,
      isPendingActionHighlighted,
      salesPerson,
    };
  }

  // Map a raw customer to ClientData. An optional meeting summary fills in the
  // aggregated fields; without it, safe defaults are used (the card fills them
  // in lazily). Customer-level fields (meetingCount, lastMeetingDate, etc.) are
  // always taken straight from the list response.
  private mapCustomerToClient(customer: any, summary?: ClientMeetingSummary): ClientData {
    const type = customer.channelPartner ? 'Channel Partner' : 'Direct';
    const status = customer.is_active !== false ? 'Active' : 'Inactive';

    // Get sales person information - try multiple sources
    const salesPerson = customer.salesPerson ||
                        customer.salesExecutive ||
                        summary?.salesPerson ||
                        'Not assigned';

    return {
      id: this.generateValidClientId(customer),
      name: customer.name || customer.email || 'Unknown',
      status,
      email: customer.email || '',
      phone: customer.phone || '',
      type,
      projectsShown: summary?.projectsShown ?? '0',
      projectsList: summary?.projectsList ?? 'No projects',
      pendingAction: summary?.pendingAction ?? 'No pending actions',
      lastMeeting: summary?.lastMeeting ?? 'No meetings',
      totalMeetings: summary?.totalMeetings ?? '0',
      isPendingActionHighlighted: summary?.isPendingActionHighlighted ?? false,
      // Additional fields
      salesPerson,
      channelPartner: customer.channelPartner,
      project_id: customer.project_id,
      budget: customer.budget,
      nextMeetingDate: customer.nextMeetingDate,
      rating: customer.rating,
      is_active: customer.is_active,
      conversation_id: customer.conversation_id,
      request_id: customer.request_id,
      meetingCount: customer.meetingCount,
      lastMeetingDate: customer.lastMeetingDate,
      address: customer.address,
    };
  }

  async getClientMeetings(clientId: string, type?: string): Promise<MeetingData[]> {
    const resolvedType = type || 'real_estate';
    const cacheKey = `${clientId}::${resolvedType}`;

    // Reuse a recent/in-flight fetch for the same client so the profile page's
    // multiple consumers don't each hit /api/meetings independently.
    const cached = this.meetingsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < ClientService.MEETINGS_CACHE_TTL_MS) {
      return cached.promise;
    }

    const promise = (async () => {
      try {
        const meetings = await this.apiCall<{ data: MeetingData[] }>('/api/meetings', {
          clientId, // server now filters by client before applying limit
          type: resolvedType,
          limit: 50 // Only this client's meetings come back, so a small limit is
                    // both correct and fast (no more over-fetching a global window).
        });

        return meetings.data || [];
      } catch (error) {
        // Don't keep a failed request cached so the next caller can retry.
        this.meetingsCache.delete(cacheKey);
        console.error('Error fetching client meetings:', error);
        return [];
      }
    })();

    this.meetingsCache.set(cacheKey, { promise, timestamp: Date.now() });
    return promise;
  }

  async getClientJourney(clientId: string, email?: string): Promise<any> {
    const params: any = {};
    if (email) params.email = email;
    if (clientId) params.customerId = clientId;
    
    return this.apiCall('/sales/customer/journey', params);
  }

  // Helper method to get client projects
  async getClientProjects(clientId: string): Promise<string[]> {
    try {
      const meetings = await this.getClientMeetings(clientId);
      const projectsSet = new Set(meetings.map(m => m.project));
      const projects = Array.from(projectsSet).filter(Boolean);
      return projects;
    } catch (error) {
      console.error('Error fetching client projects:', error);
      return [];
    }
  }

  // Helper method to get pending actions for a client
  async getClientPendingActions(clientId: string): Promise<any[]> {
    try {
      const meetings = await this.getClientMeetings(clientId);
      const pendingActions = meetings.reduce((acc, meeting) => {
        return acc.concat(meeting.pendingActions || []);
      }, [] as any[]);
      return pendingActions;
    } catch (error) {
      console.error('Error fetching pending actions:', error);
      return [];
    }
  }
}

export const clientService = new ClientService(); 